import { apiClient } from './client';
import type { ApiResponse, PagedResult } from '@/types/api.types';
import type {
  BankReconciliationDto,
  BankReconciliationListDto,
  BankReconciliationFilterDto,
  CreateBankReconciliationRequest,
  UpdateBankReconciliationRequest,
  ClearReconciliationItemsRequest,
  ParseStatementRequest,
  StatementLineDto,
} from '@/types/bankReconciliation.types';

export const bankReconciliationApi = {
  /** GET /api/v1/bank-reconciliations — paginated list */
  getList: (filter?: BankReconciliationFilterDto) =>
    apiClient.get<ApiResponse<PagedResult<BankReconciliationListDto>>>('/bank-reconciliations', { params: filter }),

  /** GET /api/v1/bank-reconciliations/{id} — full detail with lines */
  getById: (id: string) =>
    apiClient.get<ApiResponse<BankReconciliationDto>>(`/bank-reconciliations/${id}`),

  /** POST /api/v1/bank-reconciliations — create new reconciliation (auto-loads transactions) */
  create: (data: CreateBankReconciliationRequest) =>
    apiClient.post<ApiResponse<{ id: string }>>('/bank-reconciliations', data),

  /** PUT /api/v1/bank-reconciliations/{id} — update statement balances / notes */
  update: (id: string, data: UpdateBankReconciliationRequest) =>
    apiClient.put<ApiResponse<object>>(`/bank-reconciliations/${id}`, data),

  /** POST /api/v1/bank-reconciliations/{id}/clear-items — mark items cleared/uncleared */
  clearItems: (id: string, data: ClearReconciliationItemsRequest) =>
    apiClient.post<ApiResponse<object>>(`/bank-reconciliations/${id}/clear-items`, data),

  /** POST /api/v1/bank-reconciliations/{id}/complete — complete a balanced reconciliation */
  complete: (id: string) =>
    apiClient.post<ApiResponse<object>>(`/bank-reconciliations/${id}/complete`),

  /** POST /api/v1/bank-reconciliations/{id}/submit — submit a Draft reconciliation for review */
  submit: (id: string) =>
    apiClient.post<ApiResponse<object>>(`/bank-reconciliations/${id}/submit`),

  /** POST /api/v1/bank-reconciliations/{id}/reopen — reopen a completed reconciliation */
  reopen: (id: string, reason?: string) =>
    apiClient.post<ApiResponse<object>>(`/bank-reconciliations/${id}/reopen`, { reason }),

  /** DELETE /api/v1/bank-reconciliations/{id} — soft-delete a Draft reconciliation */
  delete: (id: string) =>
    apiClient.delete<ApiResponse<object>>(`/bank-reconciliations/${id}`),

  /** POST /api/v1/bank-reconciliations/parse-statement — parse a CSV bank statement (stateless) */
  parseStatement: (data: ParseStatementRequest) =>
    apiClient.post<ApiResponse<StatementLineDto[]>>('/bank-reconciliations/parse-statement', data),
};
